// Ported from Dealtracker src/appframe/tours/placement.ts (commit 30ca0cb), unchanged.
import type { Rect } from './HighlightRing';

/**
 * Where the step window goes (#153). Pure functions of rectangles, so they are unit-tested
 * without a browser: the engine (`TourRunner`) measures the target, the popups it opened and the
 * floating windows the step declares (`avoid`), then asks here for a spot that covers none of them.
 */

export type Side = 'above' | 'below' | 'before' | 'after';
export type Size = { w: number; h: number };
export type Viewport = { width: number; height: number };
export type Point = { x: number; y: number };
/** `maxHeight`: set when the window had to shrink to fit (its body then scrolls). */
export type Placed = Point & { maxHeight?: number };

/** Gap between the window and what it avoids, and the window's margin to the viewport edge. */
export const GAP = 12;
export const MARGIN = 8;
/** The shortest step window worth shrinking to: its title bar, a few lines and the buttons. */
export const MIN_HEIGHT = 140;

const right = (r: Rect) => r.left + r.width;
const bottom = (r: Rect) => r.top + r.height;
const rectAt = (p: Point, s: Size): Rect => ({ left: p.x, top: p.y, width: s.w, height: s.h });

/** Overlap with a positive area (touching edges do not count). */
export function intersects(a: Rect, b: Rect): boolean {
  return a.left < right(b) && b.left < right(a) && a.top < bottom(b) && b.top < bottom(a);
}

/** Distance between two rectangles (0 when they touch or overlap). */
function distance(a: Rect, b: Rect): number {
  const dx = Math.max(0, b.left - right(a), a.left - right(b));
  const dy = Math.max(0, b.top - bottom(a), a.top - bottom(b));
  return Math.hypot(dx, dy);
}

// Half a pixel of tolerance: rectangles come from getBoundingClientRect (fractional pixels).
function inViewport(r: Rect, vp: Viewport): boolean {
  return r.left >= MARGIN - 0.5 && r.top >= MARGIN - 0.5
    && right(r) <= vp.width - MARGIN + 0.5 && bottom(r) <= vp.height - MARGIN + 0.5;
}

const clear = (r: Rect, obstacles: readonly Rect[]) => !obstacles.some(o => intersects(r, o));
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
const uniq = (xs: number[]) => [...new Set(xs.map(v => Math.round(v)))];

const SIDE_ORDER: Record<Side, Side[]> = {
  below: ['below', 'above', 'after', 'before'],
  above: ['above', 'below', 'after', 'before'],
  after: ['after', 'before', 'below', 'above'],
  before: ['before', 'after', 'below', 'above'],
};

/**
 * Next to the target on `side`, sliding along that side (centred first, then just past each
 * obstacle) until the window covers nothing.
 */
function onSide(side: Side, target: Rect, size: Size, obstacles: readonly Rect[], vp: Viewport): Point | null {
  const vertical = side === 'above' || side === 'below';
  const main = side === 'below' ? bottom(target) + GAP
    : side === 'above' ? target.top - GAP - size.h
    : side === 'after' ? right(target) + GAP
    : target.left - GAP - size.w;
  const len = vertical ? size.w : size.h;
  const lo = MARGIN, hi = (vertical ? vp.width : vp.height) - len - MARGIN;
  const centre = vertical ? target.left + target.width / 2 - len / 2 : target.top + target.height / 2 - len / 2;
  const slides = uniq([centre, ...obstacles.flatMap(o => vertical
    ? [o.left - GAP - len, right(o) + GAP]
    : [o.top - GAP - len, bottom(o) + GAP])].map(v => clamp(v, lo, hi)))
    .sort((a, b) => Math.abs(a - centre) - Math.abs(b - centre));
  for (const s of slides) {
    const p = vertical ? { x: s, y: main } : { x: main, y: s };
    const r = rectAt(p, size);
    if (inViewport(r, vp) && clear(r, obstacles)) return p;
  }
  return null;
}

/** The free viewport corner furthest from everything to avoid. */
function corner(size: Size, obstacles: readonly Rect[], vp: Viewport): Point | null {
  const xs = [MARGIN, vp.width - size.w - MARGIN], ys = [MARGIN, vp.height - size.h - MARGIN];
  let best: Point | null = null, bestD = -1;
  for (const x of xs) for (const y of ys) {
    const r = rectAt({ x, y }, size);
    if (!inViewport(r, vp) || !clear(r, obstacles)) continue;
    const d = Math.min(...obstacles.map(o => distance(r, o)));
    if (d > bestD) { best = { x, y }; bestD = d; }
  }
  return best;
}

/** Columns worth trying: the viewport edges, the target's centre, just past each obstacle. */
function columns(target: Rect, w: number, obstacles: readonly Rect[], vp: Viewport): number[] {
  const hi = vp.width - w - MARGIN;
  return uniq([MARGIN, hi, target.left + target.width / 2 - w / 2,
    ...obstacles.flatMap(o => [o.left - GAP - w, right(o) + GAP])].map(x => clamp(x, MARGIN, hi)));
}

/** Any full-size free spot, the closest to the target. */
function anywhere(target: Rect, size: Size, obstacles: readonly Rect[], vp: Viewport): Point | null {
  const yHi = vp.height - size.h - MARGIN;
  const ys = uniq([MARGIN, yHi, ...obstacles.flatMap(o => [o.top - GAP - size.h, bottom(o) + GAP])].map(y => clamp(y, MARGIN, yHi)));
  let best: Point | null = null, bestD = Infinity;
  for (const x of columns(target, size.w, obstacles, vp)) for (const y of ys) {
    const r = rectAt({ x, y }, size);
    if (!inViewport(r, vp) || !clear(r, obstacles)) continue;
    const d = distance(r, target);
    if (d < bestD) { best = { x, y }; bestD = d; }
  }
  return best;
}

/** The tallest free vertical band, `size.w` wide, at least {@link MIN_HEIGHT} tall. */
function shrunk(target: Rect, size: Size, obstacles: readonly Rect[], vp: Viewport): Placed | null {
  let best: Placed | null = null, bestH = MIN_HEIGHT - 1, bestD = Infinity;
  for (const x of columns(target, size.w, obstacles, vp)) {
    if (x + size.w > vp.width - MARGIN + 0.5) continue;
    // The obstacles crossing this column cut [MARGIN, height - MARGIN] into free bands.
    const cuts = obstacles
      .filter(o => o.left < x + size.w && x < right(o))
      .map(o => [o.top - GAP, bottom(o) + GAP] as const)
      .sort((a, b) => a[0] - b[0]);
    let y = MARGIN;
    const bands: [number, number][] = [];
    for (const [top, end] of cuts) {
      if (top > y) bands.push([y, Math.min(top, vp.height - MARGIN)]);
      y = Math.max(y, end);
    }
    if (y < vp.height - MARGIN) bands.push([y, vp.height - MARGIN]);
    for (const [top, end] of bands) {
      const h = Math.floor(end - top);
      if (h < bestH) continue;
      const d = distance(rectAt({ x, y: top }, { w: size.w, h }), target);
      if (h > bestH || d < bestD) {
        best = h >= size.h ? { x, y: top } : { x, y: top, maxHeight: h };
        bestH = h; bestD = d;
      }
    }
  }
  return best;
}

export interface PlacementInput {
  target: Rect;
  /** Popups the target opened and floating windows the step declares. The target is implied. */
  avoid: readonly Rect[];
  size: Size;
  viewport: Viewport;
  placement?: Side;
}

/**
 * The step window's top-left (and, when it had to shrink, its max height). In order: the
 * preferred side of the target, the other sides, the free viewport corner furthest from the
 * avoid set, any free spot, then a shorter window (scrollable body). It never covers the target:
 * if nothing else fits, it gives up on the avoid set before it gives up on the target.
 */
export function computePlacement({ target, avoid, size, viewport: vp, placement = 'below' }: PlacementInput): Placed {
  const visible = avoid.filter(r => r.width > 0 && r.height > 0);
  const obstacles = [target, ...visible];
  for (const side of SIDE_ORDER[placement]) {
    const p = onSide(side, target, size, obstacles, vp);
    if (p) return p;
  }
  const found = corner(size, obstacles, vp) ?? anywhere(target, size, obstacles, vp) ?? shrunk(target, size, obstacles, vp);
  if (found) return found;
  if (visible.length > 0) return computePlacement({ target, avoid: [], size, viewport: vp, placement });
  // The target leaves no room at all (it fills the screen): stay on screen, below it if possible.
  return {
    x: clamp(target.left + target.width / 2 - size.w / 2, MARGIN, vp.width - size.w - MARGIN),
    y: clamp(bottom(target) + GAP, MARGIN, vp.height - size.h - MARGIN),
  };
}

/**
 * Moves a window the user placed out of the way of `obstacles` (popups opened since), by the
 * shortest move that clears them all. Returns `at` unchanged when nothing is covered, and null
 * when no short move clears them (the caller then falls back to {@link computePlacement}).
 */
export function nudgeOut(at: Point, size: Size, obstacles: readonly Rect[], vp: Viewport): Point | null {
  const r = rectAt(at, size);
  const hit = obstacles.filter(o => o.width > 0 && o.height > 0 && intersects(r, o));
  if (hit.length === 0) return at;
  const xHi = vp.width - size.w - MARGIN, yHi = vp.height - size.h - MARGIN;
  const candidates = hit.flatMap(o => [
    { x: o.left - GAP - size.w, y: at.y }, { x: right(o) + GAP, y: at.y },
    { x: at.x, y: o.top - GAP - size.h }, { x: at.x, y: bottom(o) + GAP },
  ]).map(p => ({ x: clamp(p.x, MARGIN, xHi), y: clamp(p.y, MARGIN, yHi) }))
    .filter(p => clear(rectAt(p, size), obstacles))
    .sort((a, b) => Math.hypot(a.x - at.x, a.y - at.y) - Math.hypot(b.x - at.x, b.y - at.y));
  return candidates[0] ?? null;
}

/** Keeps a dragged window on screen. */
export function clampToViewport(p: Point, size: Size, vp: Viewport): Point {
  return {
    x: Math.max(MARGIN, Math.min(vp.width - size.w - MARGIN, p.x)),
    y: Math.max(MARGIN, Math.min(vp.height - size.h - MARGIN, p.y)),
  };
}
